import styled from 'styled-components';

import {isNil, isNotNil} from 'ramda';

import {FinancingFieldResponseBody} from '@dms/api/core';
import {CompositionFont} from '@dms/api/sales';
import {FinancingCompositionScene} from '@dms/api/shared';
import {thumbnailUrl} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {LayerBox} from '../types/CompositionEditorAction';
import {toCssPx} from '../utils/toCssPx';
import {CanvasLayer} from './CanvasLayer';

interface EditorCanvasProps extends TestIdProps {
  scene: FinancingCompositionScene;
  scale: number;
  selectedLayerId: string | null;
  fonts: CompositionFont[];
  financingFields: FinancingFieldResponseBody[];
  previewUrl: string | null;
  // Layers whose text the last server render of this exact scene clipped.
  overflowLayerIds: string[];
  getImageUrl: (fileId: string) => string | null;
  onSelectLayer: (layerId: string | null) => void;
  onCommitLayerBox: (layerId: string, box: LayerBox) => void;
}

// The canvas element is sized from the scene (raster px × view scale), so it uses `.attrs`
// inline sizing the same way the layers do.
const CanvasSurface = styled.div.attrs<{$width: number; $height: number}>((props) => ({
  style: {width: toCssPx(props.$width), height: toCssPx(props.$height)},
}))<{$width: number; $height: number}>`
  position: relative;
  overflow: hidden;
  box-shadow: ${(props) => props.theme.shadows.elevation_2};
  background-color: ${(props) => props.theme.colors.palettes.neutral[30][100]};
`;

const ReferencePhoto = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
`;

// The checkerboard signals a transparent scene background; it is only a visual aid and is never
// part of the scene. The checker cell size follows the design grid (2 steps) rather than the scene.
const Checkerboard = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.35;
  background-image: repeating-conic-gradient(
    ${(props) => props.theme.colors.palettes.neutral[40][100]} 0% 25%,
    ${(props) => props.theme.colors.general.white} 0% 50%
  );
  background-size: ${(props) => props.theme.getSize(4)} ${(props) => props.theme.getSize(4)};
`;

const SceneBackground = styled.div.attrs<{$color: string; $opacity: number}>((props) => ({
  style: {backgroundColor: props.$color, opacity: props.$opacity},
}))<{$color: string; $opacity: number}>`
  position: absolute;
  inset: 0;
  pointer-events: none;
`;

const ServerPreview = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
`;

// Marks a layer the server render reported as overflowing, over both the render and the editable
// layers; it never takes pointer events, so the layer under it stays draggable.
const OverflowOutline = styled.div.attrs<{$box: LayerBox; $scale: number}>((props) => ({
  style: {
    left: toCssPx(props.$box.x * props.$scale),
    top: toCssPx(props.$box.y * props.$scale),
    width: toCssPx(props.$box.width * props.$scale),
    height: toCssPx(props.$box.height * props.$scale),
  },
}))<{$box: LayerBox; $scale: number}>`
  position: absolute;
  box-sizing: border-box;
  pointer-events: none;
  border: 1px dashed ${(props) => props.theme.colors.severity.danger};
`;

export function EditorCanvas(props: EditorCanvasProps) {
  const background = props.scene.canvas.background;
  const isShowingServerPreview = isNotNil(props.previewUrl);

  return (
    <CanvasSurface
      $width={props.scene.canvas.width * props.scale}
      $height={props.scene.canvas.height * props.scale}
      onClick={() => props.onSelectLayer(null)}
      data-testid={suffixTestId('canvas', props)}
    >
      <ReferencePhoto src={`/${thumbnailUrl}`} alt="" draggable={false} />
      {isNil(background) ? (
        <Checkerboard />
      ) : (
        <SceneBackground $color={background.color} $opacity={background.opacity} />
      )}
      {isShowingServerPreview ? (
        <ServerPreview
          src={props.previewUrl ?? ''}
          alt=""
          draggable={false}
          data-testid={suffixTestId('serverPreview', props)}
        />
      ) : (
        props.scene.layers.map((layer) => (
          <CanvasLayer
            key={layer.id}
            layer={layer}
            canvas={props.scene.canvas}
            scale={props.scale}
            isSelected={layer.id === props.selectedLayerId}
            fonts={props.fonts}
            financingFields={props.financingFields}
            imageUrl={layer.type === 'image' ? props.getImageUrl(layer.fileId) : null}
            onSelect={() => props.onSelectLayer(layer.id)}
            onCommit={(box) => props.onCommitLayerBox(layer.id, box)}
            data-testid={props['data-testid']}
          />
        ))
      )}
      {props.scene.layers
        .filter((layer) => props.overflowLayerIds.includes(layer.id))
        .map((layer) => (
          <OverflowOutline
            key={layer.id}
            $box={layer}
            $scale={props.scale}
            data-testid={suffixTestId(`overflow-${layer.id}`, props)}
          />
        ))}
    </CanvasSurface>
  );
}
